/**
 * Memory-pressure watchdog for long-running venue tablets.
 *
 * Chrome exposes a non-standard `performance.memory` API that reports the
 * JS heap size for the current tab. On cheap Android tablets this heap
 * grows continuously over multi-hour sessions (accumulated audio buffers,
 * React re-renders, cached track metadata) and eventually hits the OS
 * OOM-killer — the tab dies silently mid-set.
 *
 * We poll `performance.memory.usedJSHeapSize` every 60 s and:
 *   • Log the current MB reading to the console for forensics.
 *   • Once we cross the SOFT_THRESHOLD (200 MB), surface a single-shot
 *     Sonner toast asking the operator to refresh, with a "Reload" action
 *     that clears caches via the SW helper and force-reloads. We fire
 *     this at most ONCE per page session — no spam.
 *   • If we later cross the HARD_THRESHOLD (350 MB) with no user action,
 *     auto-refresh (only when audio is idle) to prevent an OOM crash.
 *
 * Firefox/Safari don't expose `performance.memory`, in which case this
 * module is a no-op.
 */

import { toast } from 'sonner';
import { forceResetAppCache } from './registerSW';

const POLL_INTERVAL_MS = 60 * 1000;              // check every minute
const SOFT_THRESHOLD_MB = 200;                    // warn the operator
const HARD_THRESHOLD_MB = 350;                    // auto-reload (idle only)
const HARD_AUTORELOAD_MB = 500;                   // force reload even mid-playback

let _started = false;
let _softToastShown = false;
let _hardReloadFired = false;
let _pollHandle = null;

function _readHeapMb() {
  const m = (typeof performance !== 'undefined') && performance.memory;
  if (!m || typeof m.usedJSHeapSize !== 'number') return null;
  return Math.round(m.usedJSHeapSize / (1024 * 1024));
}

function _showSoftWarning(mb) {
  if (_softToastShown) return;
  _softToastShown = true;
  toast.warning(`This tab is using ${mb} MB of memory.`, {
    description: 'Long sessions accumulate memory — refresh soon to keep playback smooth.',
    duration: 20_000,
    action: {
      label: 'Refresh now',
      onClick: () => forceResetAppCache().catch(() => window.location.reload()),
    },
  });
  // eslint-disable-next-line no-console
  console.warn('[memoryPressure] soft threshold crossed:', mb, 'MB');
}

async function _hardReloadIfSafe(mb) {
  if (_hardReloadFired) return;
  const isLive = typeof window !== 'undefined' && window.__spinItUpIsLive === true;
  // 350-500 MB: only reload when audio is idle so we don't clip a track.
  // 500+ MB: force reload anyway — an OOM crash costs the whole set.
  if (isLive && mb < HARD_AUTORELOAD_MB) return;
  _hardReloadFired = true;
  // eslint-disable-next-line no-console
  console.warn('[memoryPressure] hard threshold crossed — auto-reloading:', mb, 'MB');
  try { await forceResetAppCache(); } catch { window.location.reload(); }
}

export function startMemoryPressureWatch() {
  if (_started) return;
  if (typeof performance === 'undefined' || !performance.memory) {
    // eslint-disable-next-line no-console
    console.info('[memoryPressure] performance.memory unavailable — watchdog disabled');
    return;
  }
  _started = true;

  const tick = () => {
    const mb = _readHeapMb();
    if (mb == null) return;
    if (mb >= HARD_THRESHOLD_MB) {
      _hardReloadIfSafe(mb);
    } else if (mb >= SOFT_THRESHOLD_MB) {
      _showSoftWarning(mb);
    }
  };

  // Delay first check so we don't fire on the initial React hydration spike.
  setTimeout(tick, 30_000);
  _pollHandle = setInterval(tick, POLL_INTERVAL_MS);
}

export function stopMemoryPressureWatch() {
  if (_pollHandle) { clearInterval(_pollHandle); _pollHandle = null; }
  _started = false;
}
